import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import ExpandIcon from "../../assets/img/expand-icon.png";
import InfoIcon from "../../assets/img/info.svg";
import { Whisper, Tooltip } from "rsuite";
import { formatCurrency } from "../../constants/utils";
import { formatDateToMMMDDYYYY } from "../../pages/price-check/utils";

const PriceCheckDrilledDownView = ({ rowData, formValues = {} }) => {
  // Get adults and children counts from formValues
  const adultsCount = formValues.adults ?? 2;
  const childrenCount = formValues.children ?? 0;

  // Helper function to format price
  const formatPrice = (price) => {
    if (price !== undefined && price !== null && price !== 0) {
      return typeof price === "number" ? price.toFixed(2) : price.toString();
    }
    return "included";
  };

  // Helper function to extract child price from data
  const extractChildPrice = (childPriceData) => {
    if (childPriceData && typeof childPriceData === "object") {
      return childPriceData.price; // New structure: { age: 2, price: 82.5 }
    }
    return childPriceData; // Old structure: direct price value
  };

  // Helper function to format date using the same logic as formatDateToMMMDDYYYY but with different format
  const formatDateToCustomFormat = (dateString) => {
    const formatted = formatDateToMMMDDYYYY(dateString);
    // Convert "MMM-D-YYYY" to "MMM D, YYYY" format
    return formatted.replace("-", " ").replace("-", ", ");
  };

  // Helper function to add one day to a date string
  const addOneDay = (dateString) => {
    if (!dateString || typeof dateString !== "string") {
      return "";
    }

    // Create date object from the date string
    const date = new Date(dateString);

    // Check if the date is valid before proceeding
    if (isNaN(date.getTime())) {
      return "";
    }

    // Use dayjs to add one day and format back to YYYY-MM-DD
    return dayjs(dateString).add(1, "day").format("YYYY-MM-DD");
  };

  // Helper function to generate adult prices
  const generateAdultPrices = (dateKey) => {
    const adultPrices = {};
    for (let i = 0; i < adultsCount; i++) {
      const adultPrice = rowData.dailyAdultPrice?.[dateKey]?.[i];
      adultPrices[`adult${i + 1}Price`] = formatPrice(adultPrice);
    }
    return adultPrices;
  };

  // Helper function to generate child prices
  const generateChildPrices = (dateKey) => {
    const childPrices = {};
    for (let i = 0; i < childrenCount; i++) {
      const childPriceData = rowData.dailyChildPrice?.[dateKey]?.[i];
      const childPrice = extractChildPrice(childPriceData);
      childPrices[`child${i + 1}Price`] = formatPrice(childPrice);
    }
    return childPrices;
  };

  // Helper function to get daily price with fallback
  const getDailyPrice = (priceData, dateKey, dayIndex) => {
    return priceData?.[dateKey] || priceData?.[dayIndex] || "0.00";
  };

  // Helper function to create daily breakdown entry
  const createDailyEntry = (dateString, dateKey, dayIndex) => {
    const adultPrices = generateAdultPrices(dateKey);
    const childPrices = generateChildPrices(dateKey);

    return {
      date: dateString,
      ...adultPrices,
      ...childPrices,
      netPrice: getDailyPrice(rowData.dailyPrice, dateKey, dayIndex),
      tax: getDailyPrice(rowData.dailyTax, dateKey, dayIndex),
      totalPrice: getDailyPrice(rowData.dailyTotalPrice, dateKey, dayIndex),
    };
  };

  // Generate daily breakdown data from rowData
  const generateDailyBreakdown = () => {
    if (!rowData.arrivalDate || !rowData.departureDate) {
      return [];
    }

    const startDate = rowData.arrivalDate;
    const endDate = rowData.departureDate;
    const dailyBreakdown = [];

    // Generate dates from arrival to departure (excluding departure date)
    let currentDateString = startDate;
    while (currentDateString < endDate) {
      const formattedDateString = formatDateToCustomFormat(currentDateString);

      const dateKey = currentDateString;
      const dayIndex = dailyBreakdown.length;
      const dailyEntry = createDailyEntry(
        formattedDateString,
        dateKey,
        dayIndex
      );

      dailyBreakdown.push(dailyEntry);

      // Move to next date
      currentDateString = addOneDay(currentDateString);
    }

    return dailyBreakdown;
  };

  const dailyBreakdown = generateDailyBreakdown();

  return (
    <div className="drilleddown-container">
      <div className="drilleddown-header">
        <img
          src={ExpandIcon}
          alt="Expand"
          className="drilleddown-header-icon"
        />
        <div className="drilleddown-header-text">Daily breakdown</div>
      </div>
      <div className="breakdown-table-wrapper">
        <table className="breakdown-table">
          <thead>
            <tr>
              <th>Date</th>
              {/* Dynamic adult columns */}
              {Array.from({ length: adultsCount }, (_, i) => (
                <th key={`adult-${i + 1}`}>Adult {i + 1}</th>
              ))}
              {/* Dynamic child columns */}
              {Array.from({ length: childrenCount }, (_, i) => {
                const childAge = formValues.childAges?.[i];
                return (
                  <th key={`child-${i + 1}`}>
                    Child {i + 1}{" "}
                    {childAge !== null && childAge !== undefined
                      ? `(${childAge} yrs old)`
                      : ""}
                  </th>
                );
              })}
              <th>Room price</th>
            </tr>
          </thead>
          <tbody>
            {dailyBreakdown.map((day) => (
              <tr key={`day-${day.date}`}>
                <td>{day.date}</td>
                {/* Dynamic adult cells */}
                {Array.from({ length: adultsCount }, (_, i) => (
                  <td key={`adult-${i + 1}`}>
                    {day[`adult${i + 1}Price`] === "included" ? (
                      <i>included</i>
                    ) : (
                      day[`adult${i + 1}Price`]
                    )}
                  </td>
                ))}
                {/* Dynamic child cells */}
                {Array.from({ length: childrenCount }, (_, i) => (
                  <td key={`child-${i + 1}`}>
                    {day[`child${i + 1}Price`] === "included" ? (
                      <i>included</i>
                    ) : (
                      day[`child${i + 1}Price`]
                    )}
                  </td>
                ))}
                <td>{formatCurrency(day.netPrice, rowData.currency)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* Sub-totals row */}
            <tr>
              <th>Net Price</th>
              {/* Empty cells for adults and children columns */}
              {Array.from({ length: adultsCount + childrenCount }, (_, i) => (
                <th key={`subtotals-empty-${i}`}></th>
              ))}
              <th>{formatCurrency(rowData.netPrice, rowData.currency)}</th>
            </tr>

            {/* Pay at booking tax breakdown rows */}
            {rowData.payAtBookingTaxes?.map((tax) => (
              <tr
                key={`booking-tax-${tax.name}-${tax.value}`}
                data-tax-type="booking"
              >
                <td>{tax.name}</td>
                {Array.from({ length: adultsCount + childrenCount }, (_, i) => (
                  <td key={`booking-empty-${tax.name}-${i}`}></td>
                ))}
                <td>{formatCurrency(tax.value, rowData.currency)}</td>
              </tr>
            ))}

            {/* Pay at property tax breakdown rows */}
            {rowData.payAtPropertyTaxes?.map((tax) => (
              <tr
                key={`property-tax-${tax.name}-${tax.value}`}
                data-tax-type="property"
              >
                <td>{tax.name}</td>
                {Array.from({ length: adultsCount + childrenCount }, (_, i) => (
                  <td key={`property-empty-${tax.name}-${i}`}></td>
                ))}
                <td>{formatCurrency(tax.value, rowData.currency)}</td>
              </tr>
            ))}

            {/* Total row */}
            <tr className="total-row">
              <th>Total</th>
              {Array.from({ length: adultsCount + childrenCount }, (_, i) => (
                <th key={`total-empty-${i}`}></th>
              ))}
              <th>{formatCurrency(rowData.total, rowData.currency)}</th>
            </tr>

            {/* Pay at booking row */}
            <tr>
              <td>Pay at time of booking</td>
              {Array.from({ length: adultsCount + childrenCount }, (_, i) => (
                <td key={`pay-booking-empty-${i}`}></td>
              ))}
              <td>
                {formatCurrency(
                  parseFloat(rowData.netPrice || 0) +
                    (rowData.payAtBookingTaxes?.reduce(
                      (sum, tax) => sum + parseFloat(tax.value || 0),
                      0
                    ) || 0),
                  rowData.currency
                )}
              </td>
            </tr>

            {/* Pay at property row */}
            <tr>
              <td>
                Pay at property{" "}
                <Whisper
                  placement="top"
                  speaker={
                    <Tooltip className="summary-tooltip">
                      All hotels and tourist fees are paid locally directly to
                      the hotel
                    </Tooltip>
                  }
                >
                  <img
                    src={InfoIcon}
                    alt="info"
                    className="promotion-info-icon"
                    style={{ cursor: "pointer", marginLeft: "4px" }}
                  />
                </Whisper>
              </td>
              {Array.from({ length: adultsCount + childrenCount }, (_, i) => (
                <td key={`pay-property-empty-${i}`}></td>
              ))}
              <td>
                {formatCurrency(
                  parseFloat(rowData.resortFee || 0),
                  rowData.currency
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

PriceCheckDrilledDownView.propTypes = {
  rowData: PropTypes.shape({
    arrivalDate: PropTypes.string,
    departureDate: PropTypes.string,
    dailyAdultPrice: PropTypes.object,
    dailyChildPrice: PropTypes.object,
    dailyPrice: PropTypes.object,
    dailyTax: PropTypes.object,
    dailyTotalPrice: PropTypes.object,
    netPrice: PropTypes.string,
    tax: PropTypes.string,
    grossPrice: PropTypes.string,
    total: PropTypes.string,
    currency: PropTypes.string,
    resortFee: PropTypes.string,
    payAtBookingTaxes: PropTypes.array,
    payAtPropertyTaxes: PropTypes.array,
  }).isRequired,
  formValues: PropTypes.shape({
    adults: PropTypes.number,
    children: PropTypes.number,
    childAges: PropTypes.arrayOf(PropTypes.number),
  }),
};

export default PriceCheckDrilledDownView;
